/* ============================================
   BOTANE — Scroll-Autoplay-Loop-Videos
   Gehört zu snippets/botane-video-loop.liquid

   Ablauf:
   1. Beim Seitenaufbau wird kein Video-Byte geladen (<video> ohne src,
      preload="none"). Nur das Poster-Bild.
   2. ~300px bevor das Video sichtbar wird, wird die Quelle gesetzt und
      vorgeladen (Preload-Observer).
   3. Ab 40% Sichtbarkeit läuft es stumm im Loop, beim Herausscrollen
      pausiert es (Play-Observer).
   4. Ton-Button pro Video; es ist immer höchstens ein Video laut.

   prefers-reduced-motion oder fehlender IntersectionObserver:
   kein Autoplay, stattdessen nativer Player mit Controls.
   ============================================ */

(function () {
  'use strict';

  var READY_ATTR = 'data-bvl-ready';
  var PRELOAD_MARGIN = '300px 0px';
  var PLAY_THRESHOLD = 0.4;

  var reducedMotion = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supported = 'IntersectionObserver' in window;

  var videos = [];
  var preloadObserver = null;
  var playObserver = null;
  var pausedByTabChange = [];

  /* --- Quelle anhängen (erst hier entsteht der Netzwerk-Request) --- */

  function attachSource(video) {
    if (video.getAttribute('src')) return;

    var src = video.getAttribute('data-src');
    if (!src) return;

    video.setAttribute('src', src);
    video.load();
  }

  /* --- Abspielen mit Autoplay-Policy-Fallback --- */

  function tryPlay(video) {
    var promise;

    try {
      promise = video.play();
    } catch (error) {
      video.controls = true;
      return;
    }

    if (!promise || !promise.catch) return;

    promise.catch(function () {
      // Laute Wiedergabe wurde blockiert: stumm nochmal versuchen
      if (!video.muted) {
        video.muted = true;
        setSoundState(video, false);

        var retry = video.play();
        if (retry && retry.catch) {
          retry.catch(function () {
            video.controls = true;
          });
        }
        return;
      }

      video.controls = true;
    });
  }

  /* --- Ton-Button-Zustand --- */

  function soundButtonFor(video) {
    var parent = video.parentNode;
    if (!parent) return null;
    return parent.querySelector('[data-botane-video-sound]');
  }

  function setSoundState(video, on) {
    var button = soundButtonFor(video);
    if (!button) return;

    button.setAttribute('aria-pressed', on ? 'true' : 'false');

    var label = button.querySelector('.botane-video-loop__sound-label');
    if (label) {
      var next = on ? label.getAttribute('data-label-on') : label.getAttribute('data-label-off');
      if (next) label.textContent = next;
    }
  }

  function muteAllExcept(current) {
    videos.forEach(function (video) {
      if (video === current || video.muted) return;
      video.muted = true;
      video.controls = false;
      setSoundState(video, false);
    });
  }

  function onSoundClick(video) {
    return function () {
      if (video.muted) {
        muteAllExcept(video);
        video.muted = false;
        video.volume = 1;
        video.controls = true;
        setSoundState(video, true);
        tryPlay(video);
      } else {
        video.muted = true;
        video.controls = false;
        setSoundState(video, false);
      }
    };
  }

  /* --- Observer --- */

  function ensureObservers() {
    if (preloadObserver) return;

    preloadObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        attachSource(entry.target);
        preloadObserver.unobserve(entry.target);
      });
    }, { rootMargin: PRELOAD_MARGIN, threshold: 0 });

    // Bleibt dauerhaft aktiv: pausiert auch beim Herausscrollen
    playObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;

        if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD) {
          video.dataset.bvlVisible = 'true';
          attachSource(video);
          tryPlay(video);
        } else {
          delete video.dataset.bvlVisible;
          video.pause();
        }
      });
    }, { threshold: [0, PLAY_THRESHOLD] });
  }

  /* --- Fallback: wie bisher, Klick zum Abspielen --- */

  function initFallback(video) {
    video.muted = false;
    video.controls = true;
    video.setAttribute('preload', 'metadata');
    attachSource(video);

    var button = soundButtonFor(video);
    if (button && button.parentNode) button.parentNode.removeChild(button);
  }

  /* --- Init --- */

  function init() {
    var pending = document.querySelectorAll('[data-botane-video-loop]:not([' + READY_ATTR + '])');
    if (!pending.length) return;

    if (supported && !reducedMotion) ensureObservers();

    Array.prototype.forEach.call(pending, function (video) {
      video.setAttribute(READY_ATTR, '');
      videos.push(video);

      // Attribut allein reicht in Safari nicht immer
      video.muted = true;

      if (!supported || reducedMotion) {
        initFallback(video);
        return;
      }

      var button = soundButtonFor(video);
      if (button) button.addEventListener('click', onSoundClick(video));

      preloadObserver.observe(video);
      playObserver.observe(video);
    });
  }

  /* --- Tabwechsel: nicht im Hintergrund weiterlaufen --- */

  function onVisibilityChange() {
    if (document.hidden) {
      pausedByTabChange = videos.filter(function (video) {
        return !video.paused;
      });
      pausedByTabChange.forEach(function (video) {
        video.pause();
      });
      return;
    }

    pausedByTabChange.forEach(function (video) {
      if (video.dataset.bvlVisible === 'true') tryPlay(video);
    });
    pausedByTabChange = [];
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);

  // Theme-Editor: neu geladene Sections nachrüsten (idempotent über READY_ATTR)
  document.addEventListener('shopify:section:load', init);

  // Theme-Editor: Referenzen auf entfernte Sections freigeben
  document.addEventListener('shopify:section:unload', function () {
    videos = videos.filter(function (video) {
      return document.body.contains(video);
    });
  });
})();
